// src/components/BulkPortalEnable.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Button,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  CheckCircle,
  Cancel,
  Send,
  Download,
} from "@mui/icons-material";
import { collection, getDocs, doc, updateDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";

// Generate secure random password
const generatePassword = () => {
  const length = 12;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

function BulkPortalEnable({ onClose }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState([]);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);

  // Fetch customers
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const customersSnapshot = await getDocs(collection(db, "customers"));
      const customersList = customersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCustomers(customersList);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setMessage({ type: "error", text: "Failed to load customers" });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredCustomers
        .filter(c => !c.portalEnabled)
        .map(c => c.id);
      setSelectedCustomers(allIds);
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleEnablePortals = async () => {
    setProcessing(true);
    setMessage(null);

    try {
      const batch = writeBatch(db);
      const credentials = [];

      for (const customerId of selectedCustomers) {
        const customer = customers.find(c => c.id === customerId);
        const password = generatePassword();
        const email = customer.email || customer.portalEmail;

        batch.update(doc(db, "customers", customerId), {
          portalEnabled: true,
          portalEmail: email,
          portalPassword: password,
          portalUpdatedAt: serverTimestamp(),
        });

        credentials.push({
          name: customer.name,
          email: email,
          password: password,
        });
      }

      await batch.commit();

      setGeneratedCredentials(credentials);
      setShowCredentialsDialog(true);
      setMessage({ 
        type: "success", 
        text: `Portal access enabled for ${selectedCustomers.length} customer(s)!` 
      });
      
      // Refresh customer list
      await fetchCustomers();
      setSelectedCustomers([]);
      setShowConfirmDialog(false);
      
    } catch (err) {
      console.error("Error enabling portals:", err);
      setMessage({ type: "error", text: "Failed to enable portal access" });
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadCredentials = () => {
    const csvContent = "Name,Email,Password\n" +
      generatedCredentials.map(c => `"${c.name}","${c.email}","${c.password}"`).join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `portal-credentials-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCopyAllCredentials = () => {
    const text = generatedCredentials
      .map(c => `${c.name}\nEmail: ${c.email}\nPassword: ${c.password}\n`)
      .join("\n---\n");
    
    navigator.clipboard.writeText(text);
    setMessage({ type: "info", text: "All credentials copied to clipboard!" });
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter((customer) =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const enabledCount = customers.filter(c => c.portalEnabled).length;
  const disabledCount = customers.filter(c => !c.portalEnabled).length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Bulk Portal Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enable portal access for multiple customers at once
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1, bgcolor: "rgba(16,185,129,0.1)" }}>
          <CardContent>
            <Typography variant="h4" sx={{ color: "#10b981", fontWeight: 700 }}>
              {enabledCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Portal Enabled
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, bgcolor: "rgba(239,68,68,0.1)" }}>
          <CardContent>
            <Typography variant="h4" sx={{ color: "#ef4444", fontWeight: 700 }}>
              {disabledCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Portal Disabled
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, bgcolor: "rgba(59,130,246,0.1)" }}>
          <CardContent>
            <Typography variant="h4" sx={{ color: "#3b82f6", fontWeight: 700 }}>
              {selectedCustomers.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selected
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Message Alert */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Actions Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={processing ? <CircularProgress size={16} /> : <Send />}
              onClick={() => setShowConfirmDialog(true)}
              disabled={selectedCustomers.length === 0 || processing}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                },
              }}
            >
              Enable Selected ({selectedCustomers.length})
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Customer Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedCustomers.length > 0 &&
                      selectedCustomers.length < filteredCustomers.filter(c => !c.portalEnabled).length
                    }
                    checked={
                      filteredCustomers.filter(c => !c.portalEnabled).length > 0 &&
                      selectedCustomers.length === filteredCustomers.filter(c => !c.portalEnabled).length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Customer Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Portal Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow
                  key={customer.id}
                  hover
                  sx={{
                    opacity: customer.portalEnabled ? 0.5 : 1,
                    bgcolor: selectedCustomers.includes(customer.id)
                      ? "rgba(59,130,246,0.05)"
                      : "transparent",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedCustomers.includes(customer.id)}
                      onChange={() => handleSelectCustomer(customer.id)}
                      disabled={customer.portalEnabled}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {customer.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell align="center">
                    {customer.portalEnabled ? (
                      <Chip
                        icon={<CheckCircle />}
                        label="Enabled"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<Cancel />}
                        label="Disabled"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Enable Portal Access?</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to enable portal access for <strong>{selectedCustomers.length}</strong> customer(s).
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Each customer will receive:
          </Typography>
          <Box component="ul" sx={{ mt: 1 }}>
            <li>A unique secure password</li>
            <li>Portal access enabled immediately</li>
            <li>An email invite (optional)</li>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            You'll be able to download or copy all credentials after enabling.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleEnablePortals}
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            {processing ? "Enabling..." : "Enable Portal Access"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog 
        open={showCredentialsDialog} 
        onClose={() => setShowCredentialsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Portal Credentials Generated</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            Portal access has been enabled for {generatedCredentials.length} customer(s)!
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            📋 Generated Login Credentials:
          </Typography>

          <Paper sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
            {generatedCredentials.map((cred, index) => (
              <Box key={index} sx={{ mb: index < generatedCredentials.length - 1 ? 2 : 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {cred.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {cred.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Password: <code>{cred.password}</code>
                </Typography>
                {index < generatedCredentials.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Paper>

          <Alert severity="warning">
            ⚠️ Save these credentials! You won't be able to see the passwords again.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Download />}
            onClick={handleDownloadCredentials}
          >
            Download CSV
          </Button>
          <Button onClick={handleCopyAllCredentials}>
            Copy All
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowCredentialsDialog(false)}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BulkPortalEnable;